import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import {
    normalizeActionTemplates,
    normalizeConditionTemplates,
    normalizeRegistryFeature,
    normalizeRegistryFeatures,
    normalizeRegistryManifest,
    normalizeRegistryProviders
} from './adapters';
import { buildTradeLogicAuthoringFeatureListInput } from './authoringFeatureQuery';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryFeatureDto,
    TradeLogicRegistryFeatureListInput,
    TradeLogicRegistryManifestDto,
    TradeLogicRegistryProviderDto,
    TradeLogicRegistrySurface
} from './types';
import type { TradeLogicKindV2 } from '@/types/appRouter';

const REGISTRY_STALE_TIME_MS = 300000;

type ValidateFeatureRefQueryInput = Extract<
    Parameters<typeof trpc.contextFeatureRegistry.validateFeatureRef.queryOptions>[0],
    { featureRef: object }
>;

interface TradeLogicRegistryHookOptions {
    enabled?: boolean;
    staleTime?: number;
}

interface TradeLogicFeatureRefValidationOptions extends TradeLogicRegistryHookOptions {
    featureRef: ValidateFeatureRefQueryInput['featureRef'] | null | undefined;
}

interface TradeLogicRegistryFeatureHookOptions extends TradeLogicRegistryHookOptions {
    providersById?: Record<string, TradeLogicRegistryProviderDto>;
}

interface TradeLogicRegistryAuthoringFeatureOptions extends TradeLogicRegistryHookOptions {
    families?: string[];
    providerIds?: string[];
    featureKeys?: string[];
    surfaces?: TradeLogicRegistrySurface[];
    definitionKind?: TradeLogicKindV2;
    discoverable?: boolean;
    requireAuthorable?: boolean;
}

export function useTradeLogicRegistryManifest(options: TradeLogicRegistryHookOptions = {}) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.getManifest.queryOptions(),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<TradeLogicRegistryManifestDto | null>(
        () => (query.data ? normalizeRegistryManifest(query.data) : null),
        [query.data]
    );

    return {
        ...query,
        rawData: query.data,
        data
    };
}

export function useTradeLogicRegistryProviders(options: TradeLogicRegistryHookOptions = {}) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.listProviders.queryOptions(),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<TradeLogicRegistryProviderDto[]>(() => normalizeRegistryProviders(query.data), [query.data]);

    const providersById = useMemo<Record<string, TradeLogicRegistryProviderDto>>(
        () => Object.fromEntries(data.map((provider) => [provider.providerId, provider])),
        [data]
    );

    return {
        ...query,
        rawData: query.data,
        data,
        providersById
    };
}

export function useTradeLogicRegistryFamilies(options: TradeLogicRegistryHookOptions = {}) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.listFamilies.queryOptions(),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<string[]>(() => [...(query.data ?? [])].sort((left, right) => left.localeCompare(right)), [query.data]);

    return {
        ...query,
        rawData: query.data,
        data
    };
}

export function useTradeLogicRegistryFeatures(
    input?: TradeLogicRegistryFeatureListInput,
    options: TradeLogicRegistryFeatureHookOptions = {}
) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.listFeatures.queryOptions(input),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<TradeLogicRegistryFeatureDto[]>(
        () => normalizeRegistryFeatures(query.data, options.providersById),
        [options.providersById, query.data]
    );

    const featuresByKey = useMemo<Record<string, TradeLogicRegistryFeatureDto>>(
        () => Object.fromEntries(data.map((feature) => [feature.featureKey, feature])),
        [data]
    );

    return {
        ...query,
        rawData: query.data,
        data,
        featuresByKey
    };
}

export function useTradeLogicRegistryFeature(featureKey: string | null | undefined, options: TradeLogicRegistryFeatureHookOptions = {}) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.getFeature.queryOptions({ featureKey: featureKey ?? '' }),
        enabled: (options.enabled ?? true) && Boolean(featureKey),
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<TradeLogicRegistryFeatureDto | null>(
        () => normalizeRegistryFeature(query.data, options.providersById),
        [options.providersById, query.data]
    );

    return {
        ...query,
        rawData: query.data,
        data
    };
}

export function useTradeLogicFeatureRefValidation({ enabled = true, featureRef, staleTime }: TradeLogicFeatureRefValidationOptions) {
    return useQuery({
        ...trpc.contextFeatureRegistry.validateFeatureRef.queryOptions({
            featureRef: featureRef ?? {
                kind: 'contextFeature',
                featureKey: ''
            }
        }),
        enabled: enabled && Boolean(featureRef?.featureKey),
        staleTime: staleTime ?? 30000
    });
}

export function useTradeLogicAuthoringFeatures(options: TradeLogicRegistryAuthoringFeatureOptions = {}) {
    const input = useMemo<TradeLogicRegistryFeatureListInput>(
        () => buildTradeLogicAuthoringFeatureListInput(options),
        [options.definitionKind, options.discoverable, options.families, options.featureKeys, options.providerIds, options.surfaces]
    );

    const query = useTradeLogicRegistryFeatures(input, options);

    const data = useMemo<TradeLogicRegistryFeatureDto[]>(() => {
        if (options.requireAuthorable === false) return query.data;
        return query.data.filter((feature) => feature.capabilities.canAuthor);
    }, [options.requireAuthorable, query.data]);

    const featuresByKey = useMemo<Record<string, TradeLogicRegistryFeatureDto>>(
        () => Object.fromEntries(data.map((feature) => [feature.featureKey, feature])),
        [data]
    );

    return {
        ...query,
        data,
        featuresByKey
    };
}

export function useTradeLogicActionTemplates(options: TradeLogicRegistryHookOptions = {}) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.listActionTemplates.queryOptions(),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<TradeLogicRegistryActionTemplateDto[]>(() => normalizeActionTemplates(query.data), [query.data]);

    const templatesByKey = useMemo<Record<string, TradeLogicRegistryActionTemplateDto>>(
        () => Object.fromEntries(data.map((template) => [template.templateKey, template])),
        [data]
    );

    return {
        ...query,
        rawData: query.data,
        data,
        templatesByKey
    };
}

export function useTradeLogicConditionTemplates(options: TradeLogicRegistryHookOptions = {}) {
    const query = useQuery({
        ...trpc.contextFeatureRegistry.listConditionTemplates.queryOptions(),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? REGISTRY_STALE_TIME_MS
    });

    const data = useMemo<TradeLogicRegistryConditionTemplateDto[]>(() => normalizeConditionTemplates(query.data), [query.data]);

    const templatesByKey = useMemo<Record<string, TradeLogicRegistryConditionTemplateDto>>(
        () => Object.fromEntries(data.map((template) => [template.templateKey, template])),
        [data]
    );

    return {
        ...query,
        rawData: query.data,
        data,
        templatesByKey
    };
}

export function useTradeLogicRegistryTemplates(options: TradeLogicRegistryHookOptions = {}) {
    const actionTemplatesQuery = useTradeLogicActionTemplates(options);
    const conditionTemplatesQuery = useTradeLogicConditionTemplates(options);
    const isLoading = actionTemplatesQuery.isLoading || conditionTemplatesQuery.isLoading;
    const isFetching = actionTemplatesQuery.isFetching || conditionTemplatesQuery.isFetching;
    const isError = actionTemplatesQuery.isError || conditionTemplatesQuery.isError;
    const error = actionTemplatesQuery.error ?? conditionTemplatesQuery.error ?? null;

    return {
        actionTemplatesQuery,
        conditionTemplatesQuery,
        actionTemplates: actionTemplatesQuery.data,
        actionTemplatesByKey: actionTemplatesQuery.templatesByKey,
        conditionTemplates: conditionTemplatesQuery.data,
        conditionTemplatesByKey: conditionTemplatesQuery.templatesByKey,
        isLoading,
        isFetching,
        isError,
        error
    };
}
